import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import type { TransactionHistoryStatus } from 'vopay';
import { format, getTime, parseISO, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useHostNavigate } from '../HostNavigationContext';
import useUpdateHostQueryParams from '#shared/hooks/useUpdateHostQueryParams';
import useFetchTransactionsHistory from '../hooks/useFetchTransactionsHistory';
import useConfirmTransaction from '../hooks/useConfirmTransaction';
import useAccountBalances from '../hooks/useAccountBalances';
import TransactionSearchForm from './TransactionSearchForm';
import ActivityType, { ActivityTabType } from './ActivityType';
import CopyButton from './CopyButton';
import TransactionTypeLabel from './TransactionTypeLabel';
import toDayTimestamp from './toDayTimestamp';
import { TableFiltersContainer, TransactionStatus, Truncate } from '#shared/components/UI';
import styles from './Transactions.module.scss';

const {
  Table,
  Box,
  Tooltip,
  Button,
  MaterialSymbol,
  Pagination,
  formattedCurrency,
  withStyles,
  usePagination,

  createTimestamp,
  AVAILABLE_TIMEZONES,
} = vp;

const cn = withStyles(styles);

// URL query params are plain strings; these narrow them back to the filter
// unions cast-free (an unknown value falls back to the default).
function toTimezone(value: string | null | undefined): vp.Timezone | undefined {
  return AVAILABLE_TIMEZONES.find((tz) => tz === value);
}

const TRANSACTION_STATUSES: TransactionHistoryStatus[] = [
  'All',
  'requested',
  'pending',
  'in progress',
  'sent',
  'successful',
  'complete',
  'settled',
  'failed',
  'cancellation requested',
  'waiting',
  'cancelled',
  'pending approval',
];

function toTransactionStatus(value: string | null): TransactionHistoryStatus {
  return TRANSACTION_STATUSES.find((status) => status === value) ?? null;
}

type QueryParams = {
  type?: string;
  status?: TransactionHistoryStatus | null;
  flagged?: string;
  currency?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  cardTab?: string;
  settlementDate?: string | null;
  activityType?: ActivityTabType;
};

type Filters = {
  startDate: string;
  endDate: string;
  status: TransactionHistoryStatus | 'All' | null;
  flagged: string;
  type: string;
  currency: string;
  searchTerm: string;
  timeZone: vp.Timezone;
  settlementDate?: string;
};

import type { DefaultOptionProps } from 'vopay';

export type ActivitySubTableProps = {
  // The linked record the history is scoped to.
  clientAccountId: string;
  clientAccountsEnabled: boolean;
  country: string;
  // The Activity Type selector rendered inside each sub-table's header row,
  // mirroring the portal's Customer Activity tab.
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
  // Portal pages keep the filters in the page URL (deep links, reload/back
  // restoration); the drawer keeps them in memory — it must not rewrite the
  // host URL.
  persistFiltersInUrl: boolean;
  // The user's preferred timezone (default UTC).
  timezone: string | undefined;
};

type TransactionHistoryTableProps = ActivitySubTableProps & {
  // Inactive records can't remove a transaction's fraud flag (portal parity).
  active: boolean;
  showIncludeSubAccounts: boolean;
};

// The portal's Customer Activity → Transaction History table, adapted for
// the contact drawer/portal pages: filters live in memory (mirrored to the
// URL when persistFiltersInUrl), and the transaction-details link-outs go
// through the host's onNavigate (plain text when the host can't navigate).
const TransactionHistoryTable: FunctionComponent<TransactionHistoryTableProps> = ({
  clientAccountId,
  clientAccountsEnabled,
  country,
  activityTypeOptions,
  currentType,
  handleCurrentType,
  persistFiltersInUrl,
  timezone,
  active,
  showIncludeSubAccounts,
}) => {
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const hostNavigate = useHostNavigate();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Transaction.TransactionHistory.TransactionHistory',
  });

  const { t: tHistory } = useTranslation('translations', { keyPrefix: 'Shared.Transaction.TransactionHistory' });
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const { balances } = useAccountBalances();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const { confirmTransaction } = useConfirmTransaction();

  const initialFilters: Filters = {
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    status: null,
    flagged: '',
    type: '',
    currency: 'All',
    searchTerm: '',
    timeZone: toTimezone(timezone) ?? 'UTC',
  };

  // Portal mode seeds the filters from the page URL (deep links, reload/back
  // restoration); the drawer starts from the in-memory defaults.
  const [filters, setFilters] = useState<Filters>(() => {
    if (!persistFiltersInUrl) return initialFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const urlSettlementDate = urlParams.get('settlementDate');

    return {
      startDate: urlParams.get('startDate') || initialFilters.startDate,
      endDate: urlParams.get('endDate') || initialFilters.endDate,
      status: toTransactionStatus(urlParams.get('status')),
      flagged: urlParams.get('flagged') || initialFilters.flagged,
      type: urlParams.get('type') || initialFilters.type,
      currency: urlParams.get('currency') || initialFilters.currency,
      searchTerm: urlParams.get('searchTerm') || initialFilters.searchTerm,
      timeZone: toTimezone(urlParams.get('timeZone')) ?? initialFilters.timeZone,
      settlementDate: urlSettlementDate ? format(parseISO(urlSettlementDate), 'yyyy-MM-dd') : undefined,
    };
  });

  const updateQueryParams = useUpdateHostQueryParams<QueryParams>();

  const filtersApplied = Boolean(
    filters.type ||
      filters.status ||
      filters.flagged ||
      filters.searchTerm ||
      (filters.currency && filters.currency !== 'All') ||
      filters.timeZone !== initialFilters.timeZone ||
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate,
  );

  const [filteredLists, setFilteredLists] = useState<vp.TransactionsLists[]>([]);
  const [sortedList, setSortedList] = useState<vp.TransactionsLists[]>([]);

  const [sort, setSort] = useState<{
    id: string;
    order: 'none' | 'asc' | 'desc';
  } | null>(null);

  const { fetchTransactionsHistory, totalRecords } = useFetchTransactionsHistory();

  // pagination
  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchTransactionList = async (nextFilters: Filters, offset = 0) => {
    if (nextFilters.startDate > nextFilters.endDate) {
      setShouldShowEndDateError(true);

      return;
    }

    try {
      const res = await fetchTransactionsHistory({
        StartDateTime: nextFilters.startDate,
        EndDateTime: nextFilters.endDate,
        TransactionStatus: nextFilters.status === null || nextFilters.status === 'All' ? undefined : nextFilters.status,
        IsFlagged:
          nextFilters.flagged === 'All' || nextFilters.flagged === '' ? undefined : nextFilters.flagged === 'Yes',
        TransactionType:
          nextFilters.type === 'All' ||
          nextFilters.type === '' ||
          nextFilters.type === 'Refund' ||
          nextFilters.type === 'Auto Balance Transfer'
            ? undefined
            : nextFilters.type,
        Currency: nextFilters.currency,
        SearchText: nextFilters.searchTerm,
        Timezone: nextFilters.timeZone,
        SettlementDate: nextFilters.settlementDate,
        Limit: PER_PAGE,
        Offset: offset,
        [accountIDType]: clientAccountId,
      });

      setFilteredLists(
        res.Transactions.map((transaction) => ({
          ...transaction,
          TransactionType: ModifyType(transaction.TransactionType, transaction.Notes),
        })),
      );

      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching transaction history';
      alertDialog.error('Error', msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);

    fetchTransactionList(filters, offset);

    if (persistFiltersInUrl) {
      updateQueryParams({
        cardTab: 'activity',
        activityType: 'transactionHistory',
      });
    }
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(filters.startDate > filters.endDate);
  }, [filters.endDate]);

  useEffect(() => {
    if (sort && sort.order !== 'none') {
      const { id, order } = sort;
      const direction = order === 'asc' ? 1 : -1;

      const comparators: { [key: string]: (a: vp.TransactionsLists, b: vp.TransactionsLists) => number } = {
        // The wire datetime format sorts lexicographically.
        transactionDate: (a, b) => (a.TransactionDateTime > b.TransactionDateTime ? 1 : -1),
        transId: (a, b) => (a.TransactionID > b.TransactionID ? 1 : -1),
        name: (a, b) => (a.FullName > b.FullName ? 1 : -1),
        type: (a, b) => (a.TransactionType > b.TransactionType ? 1 : -1),
        referenceNumber: (a, b) => (a.ClientReferenceNumber > b.ClientReferenceNumber ? 1 : -1),
        credit: (a, b) => parseFloat(a.CreditAmount) - parseFloat(b.CreditAmount),
        debit: (a, b) => parseFloat(a.DebitAmount) - parseFloat(b.DebitAmount),
        status: (a, b) => (a.TransactionStatus > b.TransactionStatus ? 1 : -1),
        notes: (a, b) => (a.Notes > b.Notes ? 1 : -1),
      };

      const comparator = comparators[id];

      setSortedList(comparator ? [...filteredLists].sort((a, b) => direction * comparator(a, b)) : filteredLists);
    } else {
      setSortedList(filteredLists);
    }
  }, [filteredLists, sort]);

  const tableProps: vp.TableProps<vp.TransactionsLists> = {
    id: 'transactionHistoryTable',
    version: '2',
    onSort: (params) => {
      setSort(params);
    },
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('Table.transactionDate', { defaultValue: 'Trans. Date' }),
        align: 'left',
        sortable: true,

        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gap="sm" ai="center">
              {transaction.data.TransactionFlag !== '' && (
                <Tooltip
                  information={transaction.data.TransactionFlag}
                  hasButton={false}
                  customElement={
                    <Button
                      isDisabled={!active}
                      color="none"
                      background="transparent"
                      label={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                      noPadding
                      noShadow
                      onClick={async () => {
                        const result = await alertDialog.caution(
                          t('Dialogue.title', { defaultValue: 'Flag Information' }),
                          <Box gap="sm">
                            {transaction.data.TransactionFlag} <CopyButton content={transaction.data.TransactionFlag} />
                          </Box>,
                          {
                            abortLabel: t('Dialogue.closeButton', { defaultValue: 'Close' }),
                            confirmLabel: t('Dialogue.removeFlagButton', { defaultValue: 'Remove Flag' }),
                          },
                        );

                        if (result === 'confirm') {
                          blockingLoader.show();

                          try {
                            await confirmTransaction({ TransactionID: transaction.data.TransactionID });
                            await fetchTransactionList(filters);
                          } catch (error) {
                            const msg = error instanceof Error ? error.message : 'Unknown error';
                            alertDialog.error('Error', msg);
                          } finally {
                            blockingLoader.hide();
                          }
                        }
                      }}
                    />
                  }
                  position="right"
                />
              )}

              <span>{transaction.data.TransactionDateTime}</span>
            </Box>
          );
        },
      },
      {
        id: 'transId',
        header: t('Table.id', { defaultValue: 'Trans. ID' }),
        align: 'left',
        sortable: true,
        style: {
          minWidth: '100px',
        },
        template: ({ data }) => {
          return (
            <Box gap="sm" ai="center">
              {/* Link-out via the host's onNavigate; plain text when the host can't navigate. */}
              {hostNavigate ? (
                <button
                  type="button"
                  className={styles.linkOut}
                  onClick={() =>
                    hostNavigate(
                      `/transactions-history/details/${vp.removeWhiteSpaceURI(data.TransactionType)}/${
                        data.TransactionID
                      }/${data.Currency}`,
                      {
                        transactionId: data.TransactionID,
                        hasErrorInfo: data.TransactionStatus === 'failed',
                        failureReason: data.TransactionFailureReason,
                        clientAccountId: data.ClientAccountID,
                      },
                    )
                  }
                >
                  {data.TransactionID}
                </button>
              ) : (
                <span>{data.TransactionID}</span>
              )}

              <CopyButton content={data.TransactionID} color="rgba(45, 45, 45, 0.6)" />
            </Box>
          );
        },
      },
      {
        id: 'name',
        header: t('Table.name', { defaultValue: 'Name' }),
        align: 'left',
        field: 'FullName',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'type',
        header: t('Table.type', { defaultValue: 'Type' }),
        align: 'left',
        sortable: true,
        template: (transaction) => {
          return <TransactionTypeLabel transactionType={transaction.data.TransactionType} />;
        },
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'paymentRailID',
        header: t('Table.paymentRailID', { defaultValue: 'Payment Rail ID' }),
        align: 'left',
        field: 'PaymentRailID',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'referenceNumber',
        header: t('Table.referenceNumber', { defaultValue: 'Ref. Number' }),
        align: 'left',
        field: 'ClientReferenceNumber',
        sortable: true,
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'credit',
        header: t('Table.credit', { defaultValue: 'Credit' }),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.CreditAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.CreditAmount)}`}</span>

                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('Table.pending', { defaultValue: 'Pending' })}{' '}
                      {`${transaction.data.Currency} ${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'debit',
        header: t('Table.debit', { defaultValue: 'Debit' }),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.DebitAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.DebitAmount)}`}</span>

                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('Table.pending', { defaultValue: 'Pending' })}{' '}
                      {`${transaction.data.Currency} ${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Status' }),
        align: 'left',
        sortable: true,
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gapSize="10px">
              <TransactionStatus
                status={transaction.data.TransactionStatus}
                label={
                  vp.capitalizeFirstLetter(
                    tHistory(`Statuses.${transaction.data.TransactionStatus}`, {
                      defaultValue: transaction.data.TransactionStatus,
                    }),
                  ) || ''
                }
              />

              {transaction.data.TransactionFailureReason && (
                <Tooltip
                  information={transaction.data.TransactionFailureReason}
                  icon="info"
                  variant="outlined"
                  iconWidth="14px"
                  buttonWidth="22px"
                  hasButton={true}
                  position="bottomLeft"
                />
              )}
            </Box>
          );
        },
      },
      {
        id: 'settlementDate',
        header: t('Table.settlementDate', { defaultValue: 'Settlement Date' }),
        align: 'left',
        field: 'SettlementDate',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'notes',
        header: t('Table.notes', { defaultValue: 'Notes' }),
        align: 'left',
        sortable: true,
        style: {
          minWidth: '220px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <>
              <Tooltip
                information={transaction.data.Notes}
                isDisabled={!isTruncate}
                hasButton={false}
                customElement={
                  <Box gap="sm" ai="center" jc="sb" className="notes">
                    <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />

                    {transaction.data.Notes && (
                      <CopyButton content={transaction.data.Notes} color="rgba(45, 45, 45, 0.6)" />
                    )}
                  </Box>
                }
                position="bottomLeft"
              />
            </>
          );
        },
      },
    ],
    rows:
      sortedList.map((transaction): { id: string; data: vp.TransactionsLists } => ({
        id: transaction.TransactionID,
        data: transaction,
      })) || [],
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />

          <TableFiltersContainer.Trigger />
        </Box>

        <TableFiltersContainer.Content>
          <TransactionSearchForm
            country={country}
            type={filters.type}
            status={filters.status === 'All' ? null : filters.status}
            flagged={filters.flagged}
            currency={filters.currency}
            searchTerm={filters.searchTerm}
            timeZone={filters.timeZone}
            balances={balances}
            settlementDate={createTimestamp(
              filters.settlementDate ? getTime(parseISO(filters.settlementDate)) : undefined,
            )}
            showIncludeSubAccounts={showIncludeSubAccounts}
            initialTimeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            onSearch={(searchParams) => {
              const next: Filters = {
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                status: searchParams.status,
                flagged: searchParams.flagged,
                type: searchParams.type,
                currency: searchParams.currency,
                searchTerm: searchParams.searchTerm,
                timeZone: searchParams.timeZone,
                settlementDate: searchParams.settlementDate
                  ? format(new Date(searchParams.settlementDate), 'yyyy-MM-dd')
                  : undefined,
              };

              if (persistFiltersInUrl) {
                updateQueryParams({
                  type: next.type,
                  status: next.status,
                  flagged: next.flagged,
                  currency: next.currency,
                  searchTerm: next.searchTerm,
                  startDate: next.startDate,
                  endDate: next.endDate,
                  settlementDate: next.settlementDate,
                  timeZone: next.timeZone,
                });
              }

              setFilters(next);
              fetchTransactionList(next, setPageIndex(0));
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={cn('tableContainer', 'scrollbar')}>
        <Table {...tableProps} />
      </div>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              fetchTransactionList(filters, setPageIndex(e.selected));
            }}
          />
        )}
      </Box>

      {initialLoad && filteredLists && filteredLists.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('Transactions.noRecords', { defaultValue: 'There are no records to display within this date range' })}
          </Box>
        </Box>
      )}
    </Box>
  );

  function ModifyType(transactionType: vp.TransactionTypesOptions, notes: string): vp.TransactionTypesOptions {
    if (
      (transactionType === 'EFT Withdrawal' || transactionType === 'ACH Withdrawal') &&
      notes === 'Scheduled Auto Balance Transfer'
    ) {
      return 'Auto Balance Transfer';
    }

    return transactionType;
  }
};

export default TransactionHistoryTable;
